import { useCallback, useEffect, useState } from 'react';

import { useBoothUserLogout } from '@auth-v2';
import { useAtomValue } from 'jotai';

import { activeSwingIdAtom } from '@store';

import { ConfirmActiveModal } from '../components/ConfirmActiveModal/ConfirmActiveModal';
import { useInactivityLogout } from '../hooks/useInactivityLogout';

/**
 * This wrapper stabilizes auth callbacks from `useKiosk`.
 * The returned `logout` functions are not stable, which causes
 * re-renders and breaks hooks that relies on stable props and contexts.
 *
 * @returns A memoized component that starts an inactivity timer and logs out
 *          when the inactivity threshold is reached.
 */

export function SessionInactivityManager() {
    const { mutateAsync: logout } = useBoothUserLogout();

    const activeSwingId = useAtomValue(activeSwingIdAtom);
    const [showWarningModal, setShowWarningModal] = useState(false);

    const onWarning = useCallback(() => {
        setShowWarningModal(true);
    }, []);

    const { reset, logoutAt } = useInactivityLogout({
        onLogout: logout,
        onWarning,
    });
    const handleDismiss = useCallback(() => {
        reset();
        setShowWarningModal(false);
    }, [reset]);

    useEffect(() => {
        reset();
    }, [reset]);

    // If the user is not interacting with the ipad and just using the floor screen and making swings
    // we reset on new active swing id.
    useEffect(() => {
        if (activeSwingId) {
            reset();
        }
    }, [activeSwingId, reset]);

    return showWarningModal ? (
        <ConfirmActiveModal open={showWarningModal} onDismiss={handleDismiss} onAccept={logout} logoutAt={logoutAt} />
    ) : null;
}
